/* Helium Deploy — PostHog custom click events
 * Layer on top of PostHog autocapture for high-value, structured events.
 * Autocapture handles every other click; this file enriches the ones we want
 * to filter/funnel on (product handle, position, source widget).
 *
 * All capture calls are wrapped in `if (window.posthog)` — fails-soft if
 * PostHog hasn't loaded.
 *
 * Last updated: 2026-05-25
 */
(function () {
  'use strict';

  function capture(name, props) {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(name, props || {});
      }
    } catch (e) { /* swallow */ }
  }

  function handleFromHref(href) {
    if (!href) return null;
    var m = href.match(/\/products\/([^/?#]+)/);
    if (m) return m[1];
    var c = href.match(/\/collections\/([^/?#]+)/);
    if (c) return 'collection:' + c[1];
    return null;
  }

  function safe(fn) {
    try { fn(); } catch (e) { /* don't break the page */ }
  }

  // Wait for DOM ready (defer attr means we're typically past this already,
  // but be defensive).
  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else { fn(); }
  }

  ready(function () {
    var path = window.location.pathname || '';

    // ── HOMEPAGE INSTRUMENTATION ──────────────────────────────────────────
    if (path === '/' || path === '' || /^\/?(\?|#|$)/.test(path)) {

      // 1) Comparison-table "View product" CTAs (the 4 miner CTAs at the
      //    bottom of <table class="compare-table">).
      safe(function () {
        var ctaRow = document.querySelector('.compare-table .compare-cta-row');
        if (!ctaRow) return;
        var cells = ctaRow.querySelectorAll('td');
        cells.forEach(function (td, idx) {
          var a = td.querySelector('a[href]');
          if (!a) return;
          a.addEventListener('click', function () {
            capture('comparison_table_view_product_clicked', {
              product_handle: handleFromHref(a.getAttribute('href')),
              position: idx,
              cta_text: (a.textContent || '').trim(),
              href: a.getAttribute('href')
            });
          });
        });
      });

      // 2) "Get the full comparison" lead-magnet trigger.
      safe(function () {
        var btn = document.getElementById('compare-toggle');
        if (!btn) return;
        btn.addEventListener('click', function () {
          capture('comparison_table_lead_magnet_clicked', {
            cta_text: (btn.textContent || '').trim()
          });
        });
      });

      // 3) Comparison lead-magnet form submit.
      safe(function () {
        var form = document.getElementById('comparison-form');
        if (!form) return;
        form.addEventListener('submit', function () {
          var emailField = form.querySelector('input[type="email"], input[name*="email" i]');
          capture('comparison_table_lead_magnet_submitted', {
            has_email: !!(emailField && emailField.value)
          });
        });
      });

      // 4) Hero CTAs — the top "View products" buttons that anchor to #pricing.
      safe(function () {
        document.querySelectorAll('a.btn-lime[href="#pricing"], a.btn[href="#pricing"]').forEach(function (a) {
          a.addEventListener('click', function () {
            capture('hero_cta_clicked', {
              cta_text: (a.textContent || '').trim(),
              location: 'homepage'
            });
          });
        });
      });

      // 5) Any "View product" / "View bundle" link in pricing/featured rows
      //    on the homepage (outside the comparison table — featured product
      //    cards and bundles at the top of the page).
      safe(function () {
        var area = document.querySelector('main') || document.body;
        var links = area.querySelectorAll('a.btn-lime[href*="/products/"], a.btn-lime[href*="/collections/"]');
        links.forEach(function (a) {
          // Skip if it's already wired up by the comparison-table handler.
          if (a.closest('.compare-cta-row')) return;
          a.addEventListener('click', function () {
            capture('homepage_product_card_clicked', {
              product_handle: handleFromHref(a.getAttribute('href')),
              cta_text: (a.textContent || '').trim(),
              href: a.getAttribute('href')
            });
          });
        });
      });
    }

    // ── PRODUCT PAGES — Add to Cart ──────────────────────────────────────
    safe(function () {
      // Shopify product forms post to /cart/add. Wire on submit (catches
      // Enter-key + click).
      document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
        form.addEventListener('submit', function () {
          var sel = form.querySelector('select[name="id"], input[name="id"]');
          var qty = form.querySelector('input[name="quantity"]');
          var handleMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
          capture('add_to_cart_clicked', {
            variant_id: sel ? sel.value : null,
            quantity: qty ? Number(qty.value) || 1 : 1,
            product_handle: handleMatch ? handleMatch[1] : null,
            page_path: window.location.pathname
          });
        });
      });

      // Also catch the bare button click (some themes intercept submit
      // before our listener fires).
      document.querySelectorAll('.product-form__submit, button[name="add"]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var handleMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
          capture('add_to_cart_button_clicked', {
            product_handle: handleMatch ? handleMatch[1] : null,
            page_path: window.location.pathname,
            button_text: (btn.textContent || '').trim().slice(0, 80)
          });
        });
      });
    });

    // ── CART PAGE — Checkout init ────────────────────────────────────────
    if (/^\/cart(\/|$|\?)/.test(path)) {
      safe(function () {
        document.querySelectorAll('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]').forEach(function (el) {
          el.addEventListener('click', function () {
            capture('checkout_button_clicked', {
              page_path: window.location.pathname,
              cta_text: (el.textContent || el.value || '').trim().slice(0, 80)
            });
          });
        });
      });
    }

    // ── BATCH PROGRESS WIDGET (if/when it ships on PDP or homepage) ──────
    safe(function () {
      document.querySelectorAll('[data-batch-progress], .batch-progress, #batch-progress').forEach(function (el) {
        // Fire once when the widget enters viewport, so we know it's actually seen.
        if (!('IntersectionObserver' in window)) return;
        var seen = false;
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !seen) {
              seen = true;
              capture('batch_progress_widget_viewed', {
                page_path: window.location.pathname,
                sku: el.getAttribute('data-sku') || null,
                percent: el.getAttribute('data-percent') || null
              });
              io.disconnect();
            }
          });
        }, { threshold: 0.5 });
        io.observe(el);

        el.addEventListener('click', function () {
          capture('batch_progress_widget_clicked', {
            page_path: window.location.pathname,
            sku: el.getAttribute('data-sku') || null
          });
        });
      });
    });
  });
})();
